/**
 * Spec Phase 11 Slice 11D — Generate Everything confirmation dialog.
 *
 * Lists the empty drafted sections that will be processed,
 * surfaces the cap-blocked banner when the backend pre-flight
 * 429s, and accepts an optional steering textarea applied to
 * every section.
 *
 * Cost / spend information is intentionally NOT shown here —
 * per the user's instruction LLM spend is admin-only. The dialog
 * shows only section count + ETA derived from a flat 8s/section
 * estimate.
 */
import React, { useMemo, useState } from 'react';
import { Loader2, WandSparkles, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

// Per-kind minimum word count for the repair-short surface.
// Mirrors the floor parsed from backend
// SECTION_WORD_COUNT_TARGETS via _section_word_target_range —
// the same source the backend re-applies at job creation. Kept
// here as a flat dict (not parsed at runtime) so the dialog
// stays a synchronous render. Sections whose targets are
// shape-driven (per-figure prose, USPTO 150-word ceiling, …)
// are intentionally absent — repair-short doesn't apply to
// them.
const SECTION_MIN_WORDS_FE: Record<string, number> = {
  technical_field:           80,
  background_art:            300,
  summary:                   400,
  detailed_desc_embodiments: 1200,
  industrial_applicability:  150,
  cross_reference:           50,
};

interface Props {
  onClose: () => void;
  /** Called when the user clicks "Generate everything" and the
   * job either started or was cap-blocked. */
  onResult?: (kind: 'started' | 'cap_blocked' | 'error') => void;
}

export default function GenerateEverythingDialog({
  onClose, onResult,
}: Props): JSX.Element {
  const sections = useSpecStore((s) => s.sections);
  const paragraphsBySection = useSpecStore((s) => s.paragraphsBySection);
  const startGenerateEverything = useSpecStore(
    (s) => s.startGenerateEverything,
  );
  const capBlocked = useSpecStore((s) => s.generateEverythingCapBlocked);

  const [steering, setSteering] = useState('');
  const [skipExaminer, setSkipExaminer] = useState(false);
  const [repairShort, setRepairShort] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Eligible = non-auto, non-frozen, no paragraphs yet.
  const eligible = useMemo(() => {
    return sections.filter((s) => {
      if (s.is_auto_generated || s.is_frozen) return false;
      const list = paragraphsBySection[s.id] ?? [];
      return list.length === 0;
    });
  }, [sections, paragraphsBySection]);

  // Repair-short candidates = non-auto, non-frozen, NON-empty,
  // and below the per-kind floor mirrored from
  // backend SECTION_WORD_COUNT_TARGETS minimums (the same source
  // _section_word_target_range parses). Drift on a value here
  // would only affect the UX hint — the backend re-filters at
  // job creation time.
  const belowTarget = useMemo(() => {
    return sections.filter((s) => {
      if (s.is_auto_generated || s.is_frozen) return false;
      const list = paragraphsBySection[s.id] ?? [];
      if (list.length === 0) return false;
      const floor = SECTION_MIN_WORDS_FE[s.section_kind];
      if (!floor) return false;
      const total = list.reduce(
        (n, p) => n + (p.word_count || 0), 0,
      );
      return total < floor;
    });
  }, [sections, paragraphsBySection]);
  const belowTargetCount = belowTarget.length;

  // ETA reflects everything the run will touch — empty + (when
  // repair-short is enabled) the short-row replacements too.
  const totalToRun = eligible.length
    + (repairShort ? belowTargetCount : 0);
  const etaSeconds = totalToRun * 8;

  const handleSubmit = async (): Promise<void> => {
    if (totalToRun === 0 || submitting) return;
    setSubmitting(true);
    const r = await startGenerateEverything({
      drafter_intent: steering.trim() || undefined,
      skip_examiner: skipExaminer,
      ...(repairShort ? { repair_short: true } : {}),
    });
    setSubmitting(false);
    onResult?.(r.kind);
    if (r.kind === 'started') {
      onClose();
    }
    // For cap_blocked: keep the dialog open so the inline banner
    // (rendered below) is visible. For error: showToast handled
    // it; close.
    if (r.kind === 'error') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="spec-ge-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-zinc-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
          <h3 className="text-sm font-medium text-zinc-800">
            {repairShort && belowTargetCount > 0
              ? 'Generate empty + repair short sections?'
              : 'Generate every empty section?'}
          </h3>
          <button
            type="button"
            className="rounded-sm p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-3 text-xs text-zinc-700">
          {eligible.length === 0 && belowTargetCount === 0 ? (
            <p className="text-zinc-600">
              No empty drafted sections to generate. Every section
              already has content.
            </p>
          ) : (
            <>
              <p>
                {(() => {
                  const empty = eligible.length;
                  if (repairShort && belowTargetCount > 0) {
                    return (
                      `This will generate ${empty} empty `
                      + `section${empty === 1 ? '' : 's'} and `
                      + `replace ${belowTargetCount} short `
                      + `draft${belowTargetCount === 1 ? '' : 's'}:`
                    );
                  }
                  return (
                    `This will generate drafts for ${empty} empty `
                    + `section${empty === 1 ? '' : 's'}:`
                  );
                })()}
              </p>
              <ul className="space-y-0.5 max-h-48 overflow-y-auto">
                {eligible.map((s) => (
                  <li key={s.id} className="flex items-start gap-2">
                    <span className="font-mono text-[10px] text-zinc-400 shrink-0">
                      {String(s.sequence_num).padStart(2, '0')}
                    </span>
                    <span>{s.title}</span>
                  </li>
                ))}
                {repairShort && belowTarget.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-start gap-2 text-amber-800"
                    data-testid={`spec-ge-repair-row-${s.section_kind}`}
                  >
                    <span className="font-mono text-[10px] text-amber-600 shrink-0">
                      {String(s.sequence_num).padStart(2, '0')}
                    </span>
                    <span>
                      {s.title}
                      {' '}<span className="text-[10px] text-amber-600">
                        (below target — will overwrite)
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-zinc-500">
                Estimated duration: ~
                {etaSeconds < 60
                  ? `${etaSeconds}s`
                  : `${Math.round(etaSeconds / 60)} min`}
                . An examiner review runs automatically afterwards
                {' '}unless skipped.
              </p>

              <div>
                <label
                  className="block text-[11px] font-medium text-zinc-700"
                  htmlFor="ge-steering"
                >
                  Optional steering for all sections
                </label>
                <textarea
                  id="ge-steering"
                  className="mt-1 w-full rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs text-zinc-800 outline-none focus:border-zinc-500"
                  rows={3}
                  placeholder="e.g. Use the term 'temperature sensor array' consistently."
                  value={steering}
                  onChange={(e) => setSteering(e.target.value)}
                  disabled={submitting}
                  data-testid="spec-ge-steering"
                />
              </div>

              {belowTargetCount > 0 && (
                <label
                  className="flex items-start gap-2 text-[11px] text-zinc-600"
                  data-testid="spec-ge-repair-short-label"
                >
                  <input
                    type="checkbox"
                    checked={repairShort}
                    onChange={(e) => setRepairShort(e.target.checked)}
                    data-testid="spec-ge-repair-short"
                  />
                  <span>
                    Also repair sections below minimum length
                    {' '}(will overwrite existing short drafts)
                    <span className="block text-[10px] text-zinc-500">
                      {belowTargetCount} section
                      {belowTargetCount === 1 ? '' : 's'} below
                      target length.
                    </span>
                  </span>
                </label>
              )}

              <label className="flex items-center gap-2 text-[11px] text-zinc-600">
                <input
                  type="checkbox"
                  checked={skipExaminer}
                  onChange={(e) => setSkipExaminer(e.target.checked)}
                  data-testid="spec-ge-skip-examiner"
                />
                Skip the examiner review afterwards
              </label>
            </>
          )}

          {capBlocked && (
            <div
              className="rounded-sm border border-rose-200 bg-rose-50 px-2 py-1.5 text-rose-700"
              role="alert"
              data-testid="spec-ge-cap-blocked"
            >
              {capBlocked.message}
              {capBlocked.reset_at && (
                <span className="block text-[10px] opacity-80">
                  Resets {capBlocked.reset_at.slice(0, 16)}.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-2">
          <button
            type="button"
            className="rounded-sm border border-zinc-300 bg-white px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-sm bg-zinc-900 px-3 py-1 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSubmit}
            disabled={totalToRun === 0 || submitting || !!capBlocked}
            data-testid="spec-ge-submit"
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <WandSparkles className="h-3 w-3" />
            )}
            Generate everything →
          </button>
        </div>
      </div>
    </div>
  );
}
